/**
 * QrCodeManagementService
 * Service for QR code management (CRUD, analytics, scanning)
 * Route: api/codes/qr-management
 * Controller: QrCodeManagementController.cs
 */
import { BaseService } from '../base/BaseService';
/**
 * QrCodeManagementService
 * Handles QR code CRUD, status management, analytics, and scan tracking
 */
export class QrCodeManagementService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? QrCodeManagementService.DEFAULT_SERVICE_PATH,
            basePath: QrCodeManagementService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CRUD OPERATIONS ====================
    /**
     * Create a new QR code
     * POST api/codes/qr-management
     */
    async create(request) {
        const response = await this.createRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to create QR code');
        }
        return response.data;
    }
    /**
     * Create QR code with full response
     */
    createRaw(request) {
        const url = this.buildUrl();
        return this.http.post(url, request);
    }
    /**
     * Get a single QR code by ID
     * GET api/codes/qr-management/view/{id}
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(response.message || 'QR code not found');
        }
        return response.data;
    }
    /**
     * Get QR code view with full response
     */
    getViewRaw(id) {
        const url = this.buildUrl(`view/${id}`);
        return this.http.get(url);
    }
    /**
     * Update an existing QR code
     * PUT api/codes/qr-management/{id}
     */
    async update(id, request) {
        const response = await this.updateRaw(id, request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to update QR code');
        }
        return response.data;
    }
    /**
     * Update QR code with full response
     */
    updateRaw(id, request) {
        const endpoint = this.getEndpoint('update', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, request);
    }
    /**
     * Delete a QR code
     * DELETE api/codes/qr-management/{id}
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        if (!response.success) {
            throw new Error(response.message || 'Failed to delete QR code');
        }
    }
    /**
     * Delete QR code with full response
     */
    deleteRaw(id) {
        const endpoint = this.getEndpoint('delete', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    // ==================== STATUS & DESTINATION ====================
    /**
     * Update QR code status
     * PUT api/codes/qr-management/{id}/status
     */
    async updateStatus(id, status) {
        const response = await this.updateStatusRaw(id, status);
        if (!response.success) {
            throw new Error(response.message || 'Failed to update QR code status');
        }
    }
    /**
     * Update status with full response
     */
    updateStatusRaw(id, status) {
        const endpoint = this.getEndpoint('update-status', `${id}/status`);
        const url = this.buildUrl(endpoint);
        return this.http.patch(url, { status });
    }
    /**
     * Update QR code destination URL (dynamic QR codes only)
     * PUT api/codes/qr-management/{id}/destination
     */
    async updateDestination(id, destinationUrl) {
        const response = await this.updateDestinationRaw(id, destinationUrl);
        if (!response.success) {
            throw new Error(response.message || 'Failed to update QR code destination');
        }
    }
    /**
     * Update destination with full response
     */
    updateDestinationRaw(id, destinationUrl) {
        const endpoint = this.getEndpoint('update-destination', `${id}/destination`);
        const url = this.buildUrl(endpoint);
        return this.http.patch(url, { destinationUrl });
    }
    /**
     * Regenerate QR code image
     * POST api/codes/qr-management/{id}/regenerate
     */
    async regenerateImage(id) {
        const response = await this.regenerateImageRaw(id);
        if (!response.data) {
            throw new Error(response.message || 'Failed to regenerate QR code image');
        }
        return response.data;
    }
    /**
     * Regenerate image with full response
     */
    regenerateImageRaw(id) {
        const endpoint = this.getEndpoint('regenerate', `${id}/regenerate`);
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
    // ==================== FILTERED LIST OPERATIONS ====================
    /**
     * Get filtered QR codes with pagination
     * POST api/codes/qr-management/list-view
     */
    async getFilteredList(filter) {
        const response = await this.getFilteredListRaw(filter);
        if (!response.data) {
            throw new Error(response.message || 'Failed to get filtered QR codes');
        }
        return response.data;
    }
    /**
     * Get filtered list with full response
     */
    getFilteredListRaw(filter) {
        const endpoint = this.getEndpoint('list-view', 'list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter);
    }
    /**
     * Get current user's QR codes with filtering
     * POST api/codes/qr-management/my-list-view
     */
    async getMyFilteredList(filter) {
        const response = await this.getMyFilteredListRaw(filter);
        if (!response.data) {
            throw new Error(response.message || 'Failed to get my filtered QR codes');
        }
        return response.data;
    }
    /**
     * Get my filtered list with full response
     */
    getMyFilteredListRaw(filter) {
        const endpoint = this.getEndpoint('my-list-view', 'my-list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter);
    }
    // ==================== ANALYTICS & SCANS ====================
    /**
     * Get analytics summary for a QR code
     * GET api/codes/qr-management/{id}/analytics
     */
    async getAnalytics(id, dateFrom, dateTo) {
        const response = await this.getAnalyticsRaw(id, dateFrom, dateTo);
        if (!response.data) {
            throw new Error(response.message || 'Failed to get QR code analytics');
        }
        return response.data;
    }
    /**
     * Get analytics with full response
     */
    getAnalyticsRaw(id, dateFrom, dateTo) {
        const endpoint = this.getEndpoint('analytics', `${id}/analytics`);
        const url = this.buildUrlWithParams(endpoint, { dateFrom, dateTo });
        return this.http.get(url);
    }
    /**
     * Get scan records for a QR code
     * GET api/codes/qr-management/{id}/scans
     */
    async getScans(id, page, pageSize) {
        const response = await this.getScansRaw(id, page, pageSize);
        if (!response.data) {
            throw new Error(response.message || 'Failed to get QR code scans');
        }
        return response.data;
    }
    /**
     * Get scans with full response
     */
    getScansRaw(id, page, pageSize) {
        const endpoint = this.getEndpoint('scans', `${id}/scans`);
        const url = this.buildUrlWithParams(endpoint, { page, pageSize });
        return this.http.get(url);
    }
    // ==================== BULK OPERATIONS ====================
    /**
     * Bulk update QR code statuses
     * PUT api/codes/qr-management/bulk-status
     */
    async bulkUpdateStatus(ids, status) {
        const response = await this.bulkUpdateStatusRaw(ids, status);
        if (!response.success) {
            throw new Error(response.message || 'Failed to bulk update QR code statuses');
        }
    }
    /**
     * Bulk update status with full response
     */
    bulkUpdateStatusRaw(ids, status) {
        const endpoint = this.getEndpoint('bulk-status', 'bulk-status');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, { ids, status });
    }
    // ==================== STATS ====================
    /**
     * Get overall QR code statistics
     * GET api/codes/qr-management/stats
     */
    async getStats() {
        const response = await this.getStatsRaw();
        if (!response.data) {
            throw new Error(response.message || 'Failed to get QR code stats');
        }
        return response.data;
    }
    /**
     * Get stats with full response
     */
    getStatsRaw() {
        const endpoint = this.getEndpoint('stats', 'stats');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== SETTINGS ====================
    /**
     * Get the current runtime-editable QR code settings.
     * GET api/codes/qr-management/settings
     */
    async getSettings() {
        const response = await this.getSettingsRaw();
        if (!response.data) {
            throw new Error(response.message || 'Failed to get QR code settings');
        }
        return response.data;
    }
    getSettingsRaw() {
        const endpoint = this.getEndpoint('settings', 'settings');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Persist new QR code settings (admin only). Returns the updated view.
     * PUT api/codes/qr-management/settings
     */
    async updateSettings(settings) {
        const response = await this.updateSettingsRaw(settings);
        if (!response.data) {
            throw new Error(response.message || 'Failed to update QR code settings');
        }
        return response.data;
    }
    updateSettingsRaw(settings) {
        const endpoint = this.getEndpoint('update-settings', 'settings');
        const url = this.buildUrl(endpoint);
        return this.http.put(url, settings);
    }
}
QrCodeManagementService.DEFAULT_SERVICE_PATH = 'code-generators';
QrCodeManagementService.BASE_PATH = 'api/codes/qr-management';
//# sourceMappingURL=QrCodeManagementService.js.map